import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import * as XLSX from 'xlsx';
import { censusApiService, PopulationData } from './services/censusApi';
import PopulationHeatMap from './components/PopulationHeatMap';

// Types
interface MemberAddress {
  address: string;
  name?: string;
  status?: string;
  membership?: string;
  isMember?: boolean;
  lat?: number;
  lng?: number;
  color?: string;
  fileId?: string;
}

interface FileData {
  id: string;
  name: string;
  color: string;
  addresses: MemberAddress[];
  markers: google.maps.Marker[];
}

interface AppProps {}

const App: React.FC<AppProps> = () => {
  const [fileData, setFileData] = useState<FileData[]>([]);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [geocoder, setGeocoder] = useState<google.maps.Geocoder | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showMembersOnly, setShowMembersOnly] = useState(false);

  // Planting Church state
  const [plantingChurchAddress, setPlantingChurchAddress] = useState('18751 Harney Street, Elkhorn, NE 68022');
  const [plantingChurchCoords, setPlantingChurchCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [plantingChurchColor, setPlantingChurchColor] = useState('#e53935');
  const [plantingChurchMarker, setPlantingChurchMarker] = useState<google.maps.Marker | null>(null);
  const [travelTimeRadius, setTravelTimeRadius] = useState<number>(15);
  const [travelTimeCircle, setTravelTimeCircle] = useState<any>(null);
  const [addressesInRadius, setAddressesInRadius] = useState<number>(0);
  const [addressesOutsideRadius, setAddressesOutsideRadius] = useState<number>(0);
  const [potentialPlantAddress, setPotentialPlantAddress] = useState('');
  const [potentialPlantCoords, setPotentialPlantCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [potentialPlantColor, setPotentialPlantColor] = useState('#00897b');
  const [potentialPlantMarker, setPotentialPlantMarker] = useState<google.maps.Marker | null>(null);
  const [potentialTravelTimeRadius, setPotentialTravelTimeRadius] = useState<number>(15);
  const [potentialTravelTimeCircle, setPotentialTravelTimeCircle] = useState<any>(null);
  const [pulledMembersCount, setPulledMembersCount] = useState<number>(0);
  const [currentOnlyMembersCount, setCurrentOnlyMembersCount] = useState<number>(0);
  const [potentialOnlyMembersCount, setPotentialOnlyMembersCount] = useState<number>(0);
  const [outsideBothMembersCount, setOutsideBothMembersCount] = useState<number>(0);

  // Competitive Churches state
  const [competitiveChurchColor, setCompetitiveChurchColor] = useState('#ff9800');
  const [competitiveTravelTimeRadius, setCompetitiveTravelTimeRadius] = useState<number>(5);
  const [competitiveTravelTimeCircles, setCompetitiveTravelTimeCircles] = useState<any[]>([]);
  const [competitiveAddressesInRadius, setCompetitiveAddressesInRadius] = useState<number>(0);
  const [competitiveAddressesOutsideRadius, setCompetitiveAddressesOutsideRadius] = useState<number>(0);
  const [competitiveChurchData, setCompetitiveChurchData] = useState<FileData[]>([]);
  const [isLoadingCompetitive, setIsLoadingCompetitive] = useState(false);

  // Population Density state
  const [populationData, setPopulationData] = useState<PopulationData | null>(null);
  const [isPopulationHeatMapVisible, setIsPopulationHeatMapVisible] = useState(false);
  const [populationHeatMapOpacity, setPopulationHeatMapOpacity] = useState(0.7);
  const [isLoadingPopulationData, setIsLoadingPopulationData] = useState(false);

  // Function to update color for a specific file
  const updateFileColor = (fileId: string, newColor: string) => {
    setFileData(prev => prev.map(file => {
      if (file.id === fileId) {
        // Update markers with new color
        file.markers.forEach(marker => {
          marker.setIcon({
            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="8" fill="${newColor}" stroke="white" stroke-width="2"/>
                <circle cx="12" cy="12" r="3" fill="white"/>
              </svg>
            `),
            scaledSize: new google.maps.Size(24, 24),
            anchor: new google.maps.Point(12, 12)
          });
        });
        
        return { ...file, color: newColor };
      }
      return file;
    }));
  };
  const mapRef = useRef<HTMLDivElement>(null);

  const getRadiusMeters = (travelTimeMinutes: number) => {
    const averageSpeedMph = 30;
    const travelTimeHours = travelTimeMinutes / 60;
    const distanceMiles = averageSpeedMph * travelTimeHours;
    return distanceMiles * 1609.34;
  };

  const normalizeCellValue = (value: unknown): string => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'number' || typeof value === 'boolean') return String(value).trim();
    return '';
  };

  const getFirstNonEmptyValue = (row: Record<string, unknown>, keys: string[]): string => {
    for (const key of keys) {
      const value = normalizeCellValue(row[key]);
      if (value) return value;
    }
    return '';
  };

  const buildAddressFromRow = (row: Record<string, unknown>): string => {
    const directAddress = getFirstNonEmptyValue(row, [
      'Address',
      'address',
      'ADDRESS',
      'Street Address',
      'Street',
      'Home Address',
      'Home Address Street',
      'Home Address Street Line 1',
      'Work Address Street Line 1',
      'Other Address Street Line 1'
    ]);
    const line2 = getFirstNonEmptyValue(row, [
      'Home Address Street Line 2',
      'Work Address Street Line 2',
      'Other Address Street Line 2'
    ]);
    const city = getFirstNonEmptyValue(row, ['Home Address City', 'Work Address City', 'Other Address City', 'City']);
    const state = getFirstNonEmptyValue(row, ['Home Address State', 'Work Address State', 'Other Address State', 'State']);
    const zip = getFirstNonEmptyValue(row, ['Home Address Zip Code', 'Work Address Zip Code', 'Other Address Zip Code', 'Zip', 'ZIP']);

    const street = [directAddress, line2].filter(Boolean).join(' ');
    const locality = [city, state].filter(Boolean).join(', ');
    return [street, locality, zip].filter(Boolean).join(', ').replace(/,\s*,/g, ',').trim();
  };

  const buildDisplayNameFromRow = (row: Record<string, unknown>): string =>
    getFirstNonEmptyValue(row, [
      'Name',
      'name',
      'NAME',
      'Full Name',
      'Household Name',
      'First Name',
      'Given Name',
      'Last Name'
    ]);

  const isMemberClassification = (membershipValue: string): boolean =>
    membershipValue.toLowerCase().includes('member');

  const isActiveStatus = (statusValue?: string): boolean => {
    const status = normalizeCellValue(statusValue).toLowerCase();
    return !status || status === 'active';
  };

  const shouldIncludeByToggle = (address: MemberAddress): boolean => {
    if (!isActiveStatus(address.status)) return false;
    if (!showMembersOnly) return true;
    return Boolean(address.isMember);
  };

  const getFilteredAddresses = (addresses: MemberAddress[]): MemberAddress[] =>
    addresses.filter(shouldIncludeByToggle);

  // Initialize Google Maps
  useEffect(() => {
    const initMap = () => {
      if (!mapRef.current) return;

      // Greater Omaha area coordinates (centered on the expanded region including Cass County)
      const greaterOmaha = { lat: 41.1, lng: -96.0 };
      
      const mapInstance = new google.maps.Map(mapRef.current, {
        center: greaterOmaha,
        zoom: 9, // Zoomed out more to show all counties including Cass
        styles: [
          {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }]
          }
        ]
      });

      const geocoderInstance = new google.maps.Geocoder();
      
      setMap(mapInstance);
      setGeocoder(geocoderInstance);
      console.log('Google Maps initialized successfully');
    };

    // Check if Google Maps API is already loaded
    if (window.google && window.google.maps) {
      console.log('Google Maps API already loaded');
      initMap();
      return;
    }

    // Load Google Maps API only if not already loaded
    if (!document.querySelector('script[src*="maps.googleapis.com"]')) {
      console.log('Loading Google Maps API...');
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}&libraries=geometry,visualization`;
      script.async = true;
      script.defer = true;
      script.onload = initMap;
      document.head.appendChild(script);
    } else {
      console.log('Google Maps API script already exists, waiting for load...');
      // Wait for existing script to load
      const checkGoogleMaps = setInterval(() => {
        if (window.google && window.google.maps) {
          clearInterval(checkGoogleMaps);
          initMap();
        }
      }, 100);
    }
  }, []);

  // Process Excel file
  const onDrop = async (acceptedFiles: File[]) => {
    console.log('onDrop called with files:', acceptedFiles);
    if (acceptedFiles.length === 0) {
      console.log('No files accepted');
      return;
    }

    setIsLoading(true);
    const file = acceptedFiles[0];
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        console.log('File read successfully, processing...');
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        console.log('File size:', data.length, 'bytes');
        
        const workbook = XLSX.read(data, { type: 'array' });
        console.log('Workbook sheets:', workbook.SheetNames);
        
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: '' });
        console.log('Parsed JSON data:', jsonData);

        const extractedAddresses: MemberAddress[] = jsonData.map((rawRow: any) => {
          const row = rawRow as Record<string, unknown>;
          const fullAddress = buildAddressFromRow(row);
          const name = buildDisplayNameFromRow(row);
          const status = getFirstNonEmptyValue(row, ['Status', 'status']);
          const membership = getFirstNonEmptyValue(row, ['Member', 'member', 'Membership', 'membership']);
          const isMember = isMemberClassification(membership);

          console.log('Processing row:', row); // Debug log
          console.log('Extracted address:', fullAddress);
          
          return {
            address: fullAddress,
            name,
            status,
            membership,
            isMember
          };
        }).filter(item =>
          typeof item.address === 'string' &&
          item.address.trim() !== '' &&
          isActiveStatus(item.status)
        );
        
        console.log('Extracted addresses:', extractedAddresses);
        console.log('Total addresses found:', extractedAddresses.length);

        // Create new file data entry
        const fileId = Date.now().toString();
        const defaultColor = '#4285F4'; // Default blue color
        const newFileData: FileData = {
          id: fileId,
          name: file.name,
          color: defaultColor,
          addresses: extractedAddresses.map(addr => ({ ...addr, color: defaultColor, fileId })),
          markers: []
        };

        setFileData(prev => [...prev, newFileData]);
        console.log('Starting geocoding process...');
        try {
          await geocodeAddresses(newFileData);
          console.log('Geocoding completed successfully');
        } catch (error) {
          console.error('Geocoding error:', error);
        }
      } catch (error) {
        console.error('Error processing Excel file:', error);
        alert('Error processing Excel file. Please check the format. Check console for details.');
      } finally {
        setIsLoading(false);
      }
    };

    reader.readAsArrayBuffer(file);
  };

  // Geocode addresses
  const geocodeAddresses = async (fileData: FileData) => {
    console.log('geocodeAddresses called with:', fileData.addresses.length, 'addresses');
    console.log('Geocoder available:', !!geocoder);
    console.log('Map available:', !!map);
    
    if (!geocoder || !map) {
      console.error('Geocoder or map not available');
      return;
    }

    const newMarkers: google.maps.Marker[] = [];

    for (const addressData of fileData.addresses) {
      try {
        console.log(`Geocoding address: ${addressData.address}`);
        const result = await new Promise<google.maps.GeocoderResult[]>((resolve, reject) => {
          geocoder.geocode(
            { address: `${addressData.address}, Omaha, NE` },
            (results, status) => {
              console.log(`Geocoding result for ${addressData.address}:`, status, results);
              if (status === 'OK' && results) {
                resolve(results);
              } else {
                reject(new Error(`Geocoding failed for ${addressData.address}: ${status}`));
              }
            }
          );
        });

        if (result[0]) {
          const position = result[0].geometry.location;
          
          // Update the address data with coordinates
          const updatedAddress = {
            ...addressData,
            lat: position.lat(),
            lng: position.lng()
          };
          
          const marker = new google.maps.Marker({
            position,
            map,
            title: addressData.name || addressData.address,
            icon: {
              url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="8" fill="${fileData.color}" stroke="white" stroke-width="2"/>
                  <circle cx="12" cy="12" r="3" fill="white"/>
                </svg>
              `),
              scaledSize: new google.maps.Size(24, 24),
              anchor: new google.maps.Point(12, 12)
            }
          });
          (marker as any).set('isMember', Boolean(addressData.isMember));
          (marker as any).set('status', addressData.status || '');
          (marker as any).setVisible(shouldIncludeByToggle(addressData));

          newMarkers.push(marker);
          
          // Update the address in the file data with coordinates
          setFileData(prev => prev.map(fd => 
            fd.id === fileData.id 
              ? {
                  ...fd,
                  addresses: fd.addresses.map(addr => 
                    addr.address === addressData.address 
                      ? updatedAddress 
                      : addr
                  )
                }
              : fd
          ));
        }
      } catch (error) {
        console.error(`Failed to geocode ${addressData.address}:`, error);
      }
    }

    // Update file data with new markers
    setFileData(prev => {
      const updated = prev.map(fd => 
        fd.id === fileData.id 
          ? { ...fd, markers: newMarkers }
          : fd
      );

      // Fit map to show all markers from all files
      const allMarkers = updated.reduce((acc: google.maps.Marker[], fd) => [...acc, ...fd.markers], []).concat(newMarkers);
      if (allMarkers.length > 0) {
        const bounds = new google.maps.LatLngBounds();
        allMarkers.forEach(marker => {
          const position = marker.getPosition();
          if (position) bounds.extend(position);
        });
        map.fitBounds(bounds);
      }
      return updated;
    });
  };

  // Process Competitive Church Excel file
  const onCompetitiveDrop = async (acceptedFiles: File[]) => {
    console.log('onCompetitiveDrop called with files:', acceptedFiles);
    if (acceptedFiles.length === 0) {
      console.log('No files accepted');
      return;
    }

    setIsLoadingCompetitive(true);
    const file = acceptedFiles[0];
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        console.log('Competitive church file read successfully, processing...');
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        console.log('Competitive church file size:', data.length, 'bytes');
        
        const workbook = XLSX.read(data, { type: 'array' });
        console.log('Competitive church workbook sheets:', workbook.SheetNames);
        
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: '' });
        console.log('Competitive church parsed JSON data:', jsonData);

        const extractedAddresses: MemberAddress[] = jsonData.map((rawRow: any) => {
          const row = rawRow as Record<string, unknown>;
          const fullAddress = buildAddressFromRow(row);
          const name = getFirstNonEmptyValue(row, ['Church', 'church', 'CHURCH']) || buildDisplayNameFromRow(row);

          console.log('Processing competitive church row:', row);
          console.log('Extracted competitive church address:', fullAddress);
          
          return {
            address: fullAddress,
            name: name
          };
        }).filter(item => typeof item.address === 'string' && item.address.trim() !== '');
        
        console.log('Extracted competitive church addresses:', extractedAddresses);
        console.log('Total competitive church addresses found:', extractedAddresses.length);

        // Create new competitive church file data entry
        const fileId = Date.now().toString();
        const newCompetitiveChurchData: FileData = {
          id: fileId,
          name: file.name,
          color: competitiveChurchColor,
          addresses: extractedAddresses.map(addr => ({ ...addr, color: competitiveChurchColor, fileId })),
          markers: []
        };

        setCompetitiveChurchData(prev => [...prev, newCompetitiveChurchData]);
        console.log('Starting competitive church geocoding process...');
        try {
          await geocodeCompetitiveChurches(newCompetitiveChurchData);
          console.log('Competitive church geocoding completed successfully');
        } catch (error) {
          console.error('Competitive church geocoding error:', error);
        }
      } catch (error) {
        console.error('Error processing competitive church Excel file:', error);
        alert('Error processing competitive church Excel file. Please check the format. Check console for details.');
      } finally {
        setIsLoadingCompetitive(false);
      }
    };

    reader.readAsArrayBuffer(file);
  };

  // Geocode competitive church addresses
  const geocodeCompetitiveChurches = async (fileData: FileData) => {
    console.log('geocodeCompetitiveChurches called with:', fileData.addresses.length, 'addresses');
    console.log('Geocoder available:', !!geocoder);
    console.log('Map available:', !!map);
    
    if (!geocoder || !map) {
      console.error('Geocoder or map not available');
      return;
    }

    const newMarkers: google.maps.Marker[] = [];

    for (const addressData of fileData.addresses) {
      try {
        console.log(`Geocoding competitive church address: ${addressData.address}`);
        const result = await new Promise<google.maps.GeocoderResult[]>((resolve, reject) => {
          geocoder.geocode(
            { address: `${addressData.address}, Omaha, NE` },
            (results, status) => {
              console.log(`Competitive church geocoding result for ${addressData.address}:`, status, results);
              if (status === 'OK' && results) {
                resolve(results);
              } else {
                reject(new Error(`Competitive church geocoding failed for ${addressData.address}: ${status}`));
              }
            }
          );
        });

        if (result[0]) {
          const position = result[0].geometry.location;
          
          // Update the address data with coordinates
          const updatedAddress = {
            ...addressData,
            lat: position.lat(),
            lng: position.lng()
          };
          
          const marker = new google.maps.Marker({
            position,
            map,
            title: addressData.name || addressData.address,
            zIndex: 500,
            icon: {
              url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="8" fill="${fileData.color}" stroke="white" stroke-width="2"/>
                  <circle cx="12" cy="12" r="3" fill="white"/>
                </svg>
              `),
              scaledSize: new google.maps.Size(24, 24),
              anchor: new google.maps.Point(12, 12)
            }
          } as any);

          newMarkers.push(marker);
          
          // Update the address in the competitive church file data with coordinates
          setCompetitiveChurchData(prev => prev.map(fd => 
            fd.id === fileData.id 
              ? {
                  ...fd,
                  addresses: fd.addresses.map(addr => 
                    addr.address === addressData.address 
                      ? updatedAddress 
                      : addr
                  )
                }
              : fd
          ));
        }
      } catch (error) {
        console.error(`Failed to geocode competitive church ${addressData.address}:`, error);
      }
    }

    // Update competitive church file data with new markers
    setCompetitiveChurchData(prev => {
      const updated = prev.map(fd => 
        fd.id === fileData.id 
          ? { ...fd, markers: newMarkers }
          : fd
      );

      // Fit map to show all markers from all files
      const allMarkers = updated.reduce((acc: google.maps.Marker[], fd) => [...acc, ...fd.markers], []).concat(newMarkers);
      if (allMarkers.length > 0) {
        const bounds = new google.maps.LatLngBounds();
        allMarkers.forEach(marker => {
          const position = marker.getPosition();
          if (position) bounds.extend(position);
        });
        map.fitBounds(bounds);
      }
      return updated;
    });
  };

  // Calculate and draw travel time circle
  const calculateTravelTimeCircle = async (center: google.maps.LatLng, travelTimeMinutes: number) => {
    if (!map) return;

    // Remove existing circle
    if (travelTimeCircle) {
      travelTimeCircle.setMap(null);
    }

    // Estimate radius based on travel time (rough approximation)
    // Average driving speed ~30 mph in urban areas
    const radiusMeters = getRadiusMeters(travelTimeMinutes);

    // Create new circle
    const circle = new (google.maps as any).Circle({
      strokeColor: plantingChurchColor,
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: plantingChurchColor,
      fillOpacity: 0.1,
      map,
      center,
      radius: radiusMeters,
    });

    setTravelTimeCircle(circle);
  };

  const calculatePotentialTravelTimeCircle = async (center: google.maps.LatLng, travelTimeMinutes: number) => {
    if (!map) return;

    if (potentialTravelTimeCircle) {
      potentialTravelTimeCircle.setMap(null);
    }

    const radiusMeters = getRadiusMeters(travelTimeMinutes);
    const circle = new (google.maps as any).Circle({
      strokeColor: potentialPlantColor,
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: potentialPlantColor,
      fillOpacity: 0.1,
      map,
      center,
      radius: radiusMeters,
    });

    setPotentialTravelTimeCircle(circle);
  };

  // Update travel time circle when travel time changes
  useEffect(() => {
    if (plantingChurchCoords && map) {
      const center = new google.maps.LatLng(plantingChurchCoords.lat, plantingChurchCoords.lng);
      calculateTravelTimeCircle(center, travelTimeRadius);
    }
  }, [travelTimeRadius, plantingChurchCoords, map, plantingChurchColor]);

  // Calculate addresses within radius
  const calculateAddressesInRadius = () => {
    console.log('calculateAddressesInRadius called');
    console.log('plantingChurchCoords:', plantingChurchCoords);
    console.log('fileData:', fileData);
    
    if (!plantingChurchCoords || !fileData.length) {
      console.log('No planting church coords or no file data');
      setAddressesInRadius(0);
      setAddressesOutsideRadius(0);
      return;
    }

    const churchLocation = new google.maps.LatLng(plantingChurchCoords.lat, plantingChurchCoords.lng);
    let inRadius = 0;
    let outsideRadius = 0;

    // Calculate radius in meters
    const radiusMeters = getRadiusMeters(travelTimeRadius);
    
    console.log('Radius in meters:', radiusMeters);

    fileData.forEach(file => {
      console.log('Processing file:', file.name, 'with', file.addresses.length, 'addresses');
      getFilteredAddresses(file.addresses).forEach(address => {
        console.log('Checking address:', address.address, 'coords:', address.lat, address.lng);
        if (address.lat && address.lng) {
          const addressLocation = new google.maps.LatLng(address.lat, address.lng);
          const distance = (google.maps as any).geometry.spherical.computeDistanceBetween(churchLocation, addressLocation);
          console.log('Distance:', distance, 'meters');
          
          if (distance <= radiusMeters) {
            inRadius++;
            console.log('Address IN radius');
          } else {
            outsideRadius++;
            console.log('Address OUTSIDE radius');
          }
        } else {
          console.log('Address has no coordinates, skipping');
        }
      });
    });

    console.log('Final counts - In radius:', inRadius, 'Outside radius:', outsideRadius);
    setAddressesInRadius(inRadius);
    setAddressesOutsideRadius(outsideRadius);
  };

  // Update address counts when relevant data changes
  useEffect(() => {
    calculateAddressesInRadius();
  }, [travelTimeRadius, plantingChurchCoords, fileData, showMembersOnly]);

  // Calculate and draw competitive church travel time circles
  const calculateCompetitiveTravelTimeCircles = async (travelTimeMinutes: number) => {
    if (!map) return;

    // Remove existing circles
    competitiveTravelTimeCircles.forEach(circle => {
      if (circle) {
        circle.setMap(null);
      }
    });

    // If travel time is 0 or negative, don't draw any circles
    if (travelTimeMinutes <= 0) {
      setCompetitiveTravelTimeCircles([]);
      return;
    }

    // Estimate radius based on travel time (rough approximation)
    const averageSpeedMph = 30;
    const travelTimeHours = travelTimeMinutes / 60;
    const distanceMiles = averageSpeedMph * travelTimeHours;
    const radiusMeters = distanceMiles * 1609.34;

    // Create circles for all competitive church locations
    const circles: any[] = [];
    
    competitiveChurchData.forEach(compFile => {
      compFile.addresses.forEach(compAddress => {
        if (compAddress.lat && compAddress.lng) {
          const center = new google.maps.LatLng(compAddress.lat, compAddress.lng);
          const circle = new (google.maps as any).Circle({
            strokeColor: compFile.color,
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: compFile.color,
            fillOpacity: 0.1,
            map,
            center,
            radius: radiusMeters,
          });
          circles.push(circle);
        }
      });
    });

    setCompetitiveTravelTimeCircles(circles);
  };

  // Calculate addresses within competitive church radius
  const calculateCompetitiveAddressesInRadius = () => {
    console.log('calculateCompetitiveAddressesInRadius called');
    console.log('competitiveChurchData:', competitiveChurchData);
    console.log('fileData:', fileData);
    
    if (!competitiveChurchData.length || !fileData.length) {
      console.log('No competitive church data or no file data');
      setCompetitiveAddressesInRadius(0);
      setCompetitiveAddressesOutsideRadius(0);
      return;
    }

    // If radius is 0, all addresses are outside the radius
    if (competitiveTravelTimeRadius <= 0) {
      const totalAddresses = fileData.reduce((total, file) => total + getFilteredAddresses(file.addresses).length, 0);
      setCompetitiveAddressesInRadius(0);
      setCompetitiveAddressesOutsideRadius(totalAddresses);
      return;
    }

    let inRadius = 0;
    let outsideRadius = 0;

    // Calculate radius in meters
    const averageSpeedMph = 30;
    const travelTimeHours = competitiveTravelTimeRadius / 60;
    const distanceMiles = averageSpeedMph * travelTimeHours;
    const radiusMeters = distanceMiles * 1609.34;
    
    console.log('Competitive radius in meters:', radiusMeters);

    fileData.forEach(file => {
      console.log('Processing file for competitive church:', file.name, 'with', file.addresses.length, 'addresses');
      getFilteredAddresses(file.addresses).forEach(address => {
        console.log('Checking address for competitive church:', address.address, 'coords:', address.lat, address.lng);
        if (address.lat && address.lng) {
          let isWithinAnyCompetitiveRadius = false;
          
          // Check against each competitive church location
          competitiveChurchData.forEach(compFile => {
            compFile.addresses.forEach(compAddress => {
              if (compAddress.lat && compAddress.lng && address.lat && address.lng) {
                const addressLocation = new google.maps.LatLng(address.lat, address.lng);
                const compLocation = new google.maps.LatLng(compAddress.lat, compAddress.lng);
                const distance = (google.maps as any).geometry.spherical.computeDistanceBetween(compLocation, addressLocation);
                console.log(`Distance from competitive church ${compAddress.address} to ${address.address}: ${distance}m`);
                
                if (distance <= radiusMeters) {
                  isWithinAnyCompetitiveRadius = true;
                  console.log('Address IN competitive radius');
                }
              }
            });
          });
          
          if (isWithinAnyCompetitiveRadius) {
            inRadius++;
          } else {
            outsideRadius++;
            console.log('Address OUTSIDE competitive radius');
          }
        } else {
          console.log('Address has no coordinates, skipping');
        }
      });
    });

    console.log('Final competitive counts - In radius:', inRadius, 'Outside radius:', outsideRadius);
    setCompetitiveAddressesInRadius(inRadius);
    setCompetitiveAddressesOutsideRadius(outsideRadius);
  };

  // Update competitive address counts when relevant data changes
  useEffect(() => {
    calculateCompetitiveAddressesInRadius();
  }, [competitiveTravelTimeRadius, fileData, competitiveChurchData, showMembersOnly]);

  // Update competitive travel time circles when travel time changes
  useEffect(() => {
    if (map) {
      calculateCompetitiveTravelTimeCircles(competitiveTravelTimeRadius);
    }
  }, [competitiveTravelTimeRadius, competitiveChurchData, map]);

  useEffect(() => {
    if (potentialPlantCoords && map) {
      const center = new google.maps.LatLng(potentialPlantCoords.lat, potentialPlantCoords.lng);
      calculatePotentialTravelTimeCircle(center, potentialTravelTimeRadius);
    }
  }, [potentialTravelTimeRadius, potentialPlantCoords, map, potentialPlantColor]);

  // Handle Planting Church address submit
  const handlePlantingChurchSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!plantingChurchAddress || !geocoder || !map) return;
    try {
      const results = await new Promise<google.maps.GeocoderResult[]>((resolve, reject) => {
        geocoder.geocode({ address: plantingChurchAddress }, (results, status) => {
          if (status === 'OK' && results && results[0]) {
            resolve(results);
          } else {
            reject(new Error('Geocoding failed: ' + status));
          }
        });
      });
      const loc = results[0].geometry.location;
      setPlantingChurchCoords({ lat: loc.lat(), lng: loc.lng() });
      // Remove old marker if exists
      if (plantingChurchMarker) plantingChurchMarker.setMap(null);
      // Add new marker
      const marker = new google.maps.Marker({
        position: loc,
        map,
        title: 'Planting Church',
        zIndex: 1000,
        icon: {
          url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" fill="${plantingChurchColor}" stroke="white" stroke-width="2"/>
              <circle cx="12" cy="12" r="4" fill="white"/>
            </svg>
          `),
          scaledSize: new google.maps.Size(32, 32),
          anchor: new google.maps.Point(16, 16)
        }
      } as any);
      setPlantingChurchMarker(marker);
      
      // Calculate travel time circle
      await calculateTravelTimeCircle(loc, travelTimeRadius);
      
      // Optionally, fit map to show this marker
      (map as any).panTo(loc);
    } catch (err) {
      alert('Could not geocode address.');
    }
  };

  const geocodeWithFallbacks = async (baseAddress: string): Promise<google.maps.GeocoderResult[]> => {
    if (!geocoder) {
      throw new Error('Geocoder not initialized');
    }

    const attempts = [baseAddress, `${baseAddress}, Omaha, NE`, `${baseAddress}, NE`];
    let lastStatus = 'UNKNOWN_ERROR';

    for (const attempt of attempts) {
      try {
        const results = await new Promise<google.maps.GeocoderResult[]>((resolve, reject) => {
          geocoder.geocode({ address: attempt }, (geocodeResults, status) => {
            if (status === 'OK' && geocodeResults && geocodeResults[0]) {
              resolve(geocodeResults);
            } else {
              lastStatus = status;
              reject(new Error(status));
            }
          });
        });
        return results;
      } catch (error) {
        console.warn(`Geocode attempt failed for "${attempt}":`, error);
      }
    }

    throw new Error(`Geocoding failed after fallbacks: ${lastStatus}`);
  };

  const handlePotentialPlantSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!potentialPlantAddress || !geocoder || !map) return;

    try {
      const results = await geocodeWithFallbacks(potentialPlantAddress.trim());
      const loc = results[0].geometry.location;
      setPotentialPlantCoords({ lat: loc.lat(), lng: loc.lng() });

      if (potentialPlantMarker) {
        potentialPlantMarker.setMap(null);
      }

      const marker = new google.maps.Marker({
        position: loc,
        map,
        title: 'Potential Plant Site',
        zIndex: 1100,
        icon: {
          url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" fill="${potentialPlantColor}" stroke="white" stroke-width="2"/>
              <circle cx="12" cy="12" r="4" fill="white"/>
            </svg>
          `),
          scaledSize: new google.maps.Size(32, 32),
          anchor: new google.maps.Point(16, 16)
        }
      } as any);
      setPotentialPlantMarker(marker);

      await calculatePotentialTravelTimeCircle(loc, potentialTravelTimeRadius);
      (map as any).panTo(loc);
    } catch (error) {
      alert('Could not geocode potential site address. Try a more specific location.');
    }
  };

  // Update planting church marker color
  useEffect(() => {
    if (plantingChurchMarker) {
      plantingChurchMarker.setIcon({
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" fill="${plantingChurchColor}" stroke="white" stroke-width="2"/>
            <circle cx="12" cy="12" r="4" fill="white"/>
          </svg>
        `),
        scaledSize: new google.maps.Size(32, 32),
        anchor: new google.maps.Point(16, 16)
      });
    }
  }, [plantingChurchColor, plantingChurchMarker]);

  useEffect(() => {
    if (potentialPlantMarker) {
      potentialPlantMarker.setIcon({
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" fill="${potentialPlantColor}" stroke="white" stroke-width="2"/>
            <circle cx="12" cy="12" r="4" fill="white"/>
          </svg>
        `),
        scaledSize: new google.maps.Size(32, 32),
        anchor: new google.maps.Point(16, 16)
      });
    }
  }, [potentialPlantColor, potentialPlantMarker]);

  const calculatePulledMembers = () => {
    if (!fileData.length || !plantingChurchCoords || !potentialPlantCoords) {
      setPulledMembersCount(0);
      setCurrentOnlyMembersCount(0);
      setPotentialOnlyMembersCount(0);
      setOutsideBothMembersCount(0);
      return;
    }

    const currentCenter = new google.maps.LatLng(plantingChurchCoords.lat, plantingChurchCoords.lng);
    const potentialCenter = new google.maps.LatLng(potentialPlantCoords.lat, potentialPlantCoords.lng);
    const currentRadiusMeters = getRadiusMeters(travelTimeRadius);
    const potentialRadiusMeters = getRadiusMeters(potentialTravelTimeRadius);

    let overlapCount = 0;
    let currentOnlyCount = 0;
    let potentialOnlyCount = 0;
    let outsideBothCount = 0;

    fileData.forEach(file => {
      getFilteredAddresses(file.addresses).forEach(address => {
        if (typeof address.lat !== 'number' || typeof address.lng !== 'number') {
          return;
        }

        const memberLocation = new google.maps.LatLng(address.lat, address.lng);
        const distanceToCurrent = (google.maps as any).geometry.spherical.computeDistanceBetween(currentCenter, memberLocation);
        const distanceToPotential = (google.maps as any).geometry.spherical.computeDistanceBetween(potentialCenter, memberLocation);

        const inCurrent = distanceToCurrent <= currentRadiusMeters;
        const inPotential = distanceToPotential <= potentialRadiusMeters;

        if (inCurrent && inPotential) {
          overlapCount++;
        } else if (inCurrent) {
          currentOnlyCount++;
        } else if (inPotential) {
          potentialOnlyCount++;
        } else {
          outsideBothCount++;
        }
      });
    });

    setPulledMembersCount(overlapCount);
    setCurrentOnlyMembersCount(currentOnlyCount);
    setPotentialOnlyMembersCount(potentialOnlyCount);
    setOutsideBothMembersCount(outsideBothCount);
  };

  useEffect(() => {
    calculatePulledMembers();
  }, [
    fileData,
    plantingChurchCoords,
    travelTimeRadius,
    potentialPlantCoords,
    potentialTravelTimeRadius,
    showMembersOnly
  ]);

  useEffect(() => {
    fileData.forEach(file => {
      file.markers.forEach(marker => {
        const isMember = Boolean((marker as any).get('isMember'));
        (marker as any).setVisible(!showMembersOnly || isMember);
      });
    });
  }, [showMembersOnly, fileData]);

  // Load population data
  const loadPopulationData = async () => {
    if (populationData) return; // Already loaded
    
    setIsLoadingPopulationData(true);
    try {
      const data = await censusApiService.getOmahaPopulationData();
      setPopulationData(data);
      console.log('Population data loaded:', data);
    } catch (error) {
      console.error('Failed to load population data:', error);
      alert('Failed to load population density data. Please try again.');
    } finally {
      setIsLoadingPopulationData(false);
    }
  };





  return (
    <AppContainer>
      <Header>
        <Title>Church Planter</Title>
        <Subtitle>Geographic Data Visualization for Church Planting</Subtitle>
      </Header>
      
      <MainContent>
        <Sidebar>
          {/* Planting Church input */}
          <form onSubmit={handlePlantingChurchSubmit} style={{ marginBottom: '2rem' }}>
            <h3>Planting Church</h3>
            <input
              type="text"
              placeholder="Enter church address"
              value={plantingChurchAddress}
              onChange={e => setPlantingChurchAddress(e.target.value)}
              style={{ width: '100%', padding: '0.5rem', marginBottom: '0.5rem', borderRadius: 4, border: '1px solid #ccc' }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <input
                type="color"
                value={plantingChurchColor}
                onChange={e => setPlantingChurchColor(e.target.value)}
                style={{ width: 32, height: 32, border: 'none', borderRadius: '50%', cursor: 'pointer' }}
                disabled={!plantingChurchCoords}
              />
              <span style={{ color: '#666', fontSize: '0.9rem' }}>Pin color</span>
            </div>
            <div style={{ marginBottom: '0.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.25rem', color: '#333', fontSize: '0.9rem' }}>
                Travel Time Radius:
              </label>
              <select
                value={travelTimeRadius}
                onChange={e => setTravelTimeRadius(Number(e.target.value))}
                style={{ width: '100%', padding: '0.5rem', borderRadius: 4, border: '1px solid #ccc' }}
                disabled={!plantingChurchCoords}
              >
                <option value={5}>5 minutes</option>
                <option value={10}>10 minutes</option>
                <option value={15}>15 minutes</option>
                <option value={20}>20 minutes</option>
                <option value={30}>30 minutes</option>
                <option value={40}>40 minutes</option>
              </select>
            </div>
            {plantingChurchCoords && (
              <div style={{ 
                marginBottom: '0.5rem', 
                padding: '0.5rem', 
                backgroundColor: '#f8f9fa', 
                borderRadius: 4,
                fontSize: '0.9rem'
              }}>
                <div style={{ color: '#28a745', marginBottom: '0.25rem' }}>
                  Within radius: {addressesInRadius} addresses
                </div>
                <div style={{ color: '#dc3545' }}>
                  Outside radius: {addressesOutsideRadius} addresses
                </div>
              </div>
            )}
            <button type="submit" style={{ padding: '0.5rem 1rem', borderRadius: 4, background: '#667eea', color: 'white', border: 'none', cursor: 'pointer' }}>Set Address</button>
          </form>

          {/* Potential Plant Site input */}
          <form onSubmit={handlePotentialPlantSubmit} style={{ marginBottom: '2rem' }}>
            <h3>Potential Plant Site</h3>
            <input
              type="text"
              placeholder="Enter potential site address (e.g., 120th and HWY 370)"
              value={potentialPlantAddress}
              onChange={e => setPotentialPlantAddress(e.target.value)}
              style={{ width: '100%', padding: '0.5rem', marginBottom: '0.5rem', borderRadius: 4, border: '1px solid #ccc' }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <input
                type="color"
                value={potentialPlantColor}
                onChange={e => setPotentialPlantColor(e.target.value)}
                style={{ width: 32, height: 32, border: 'none', borderRadius: '50%', cursor: 'pointer' }}
                disabled={!potentialPlantCoords}
              />
              <span style={{ color: '#666', fontSize: '0.9rem' }}>Pin color</span>
            </div>
            <div style={{ marginBottom: '0.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.25rem', color: '#333', fontSize: '0.9rem' }}>
                Travel Time Radius:
              </label>
              <select
                value={potentialTravelTimeRadius}
                onChange={e => setPotentialTravelTimeRadius(Number(e.target.value))}
                style={{ width: '100%', padding: '0.5rem', borderRadius: 4, border: '1px solid #ccc' }}
                disabled={!potentialPlantCoords}
              >
                <option value={5}>5 minutes</option>
                <option value={10}>10 minutes</option>
                <option value={15}>15 minutes</option>
                <option value={20}>20 minutes</option>
                <option value={30}>30 minutes</option>
                <option value={40}>40 minutes</option>
              </select>
            </div>
            {potentialPlantCoords && (
              <div style={{
                marginBottom: '0.5rem',
                padding: '0.5rem',
                backgroundColor: '#f8f9fa',
                borderRadius: 4,
                fontSize: '0.9rem'
              }}>
                <div style={{ color: '#6f42c1', marginBottom: '0.25rem' }}>
                  Pulled (overlap): {pulledMembersCount} members
                </div>
                <div style={{ color: '#0d6efd' }}>
                  Current only: {currentOnlyMembersCount}
                </div>
                <div style={{ color: '#198754' }}>
                  Potential only: {potentialOnlyMembersCount}
                </div>
                <div style={{ color: '#6c757d' }}>
                  Outside both: {outsideBothMembersCount}
                </div>
              </div>
            )}
            <button type="submit" style={{ padding: '0.5rem 1rem', borderRadius: 4, background: '#0f766e', color: 'white', border: 'none', cursor: 'pointer' }}>Set Address</button>
          </form>

          {/* Member Data Upload */}
          <div style={{ marginBottom: '2rem' }}>
            <h3>Upload Member Data</h3>
            
            {/* Manual file input */}
            <div style={{marginBottom: '1rem'}}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={showMembersOnly}
                  onChange={(e) => setShowMembersOnly(e.target.checked)}
                />
                <span style={{ fontSize: '0.9rem', color: '#333' }}>
                  Members only (active households only)
                </span>
              </label>
              <p style={{fontSize: '0.9rem', color: '#666', marginBottom: '0.5rem'}}>
                Select Excel file with member addresses:
              </p>
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={(e) => {
                  const files = e.target.files;
                  if (files && files.length > 0) {
                    console.log('Manual file selection:', files[0]);
                    onDrop([files[0]]);
                  }
                }}
                style={{
                  fontSize: '0.8rem',
                  padding: '0.5rem',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  backgroundColor: '#f9f9f9',
                  width: '100%'
                }}
              />
              {isLoading && (
                <div style={{ 
                  padding: '0.5rem', 
                  backgroundColor: '#fff3cd', 
                  borderRadius: 4,
                  fontSize: '0.9rem',
                  color: '#856404',
                  marginTop: '0.5rem'
                }}>
                  Processing file...
                </div>
              )}
            </div>
            
            {fileData.length > 0 && (
              <StatsContainer>
                <h4>Data Summary</h4>
                <p>Total files: {fileData.length}</p>
                <p>Total addresses: {fileData.reduce((sum, fd) => sum + getFilteredAddresses(fd.addresses).length, 0)}</p>
                <p>
                  Total geocoded: {fileData.reduce((sum, fd) =>
                    sum + fd.markers.filter(marker => (marker as any).getVisible()).length, 0
                  )}
                </p>
              </StatsContainer>
            )}

            {fileData.length > 0 && (
              <AddressList>
                <h4>Uploaded Files</h4>
                <AddressContainer>
                  {fileData.map((file) => (
                    <FileItem key={file.id}>
                      <FileHeader>
                        <strong>{file.name}</strong>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <input
                            type="color"
                            value={file.color}
                            onChange={(e) => updateFileColor(file.id, e.target.value)}
                            style={{
                              width: '24px',
                              height: '24px',
                              border: 'none',
                              borderRadius: '50%',
                              cursor: 'pointer'
                            }}
                          />
                          <ColorIndicator style={{ backgroundColor: file.color }} />
                        </div>
                      </FileHeader>
                      <span>
                        {getFilteredAddresses(file.addresses).length} addresses • {file.markers.filter(marker => (marker as any).getVisible()).length} geocoded
                      </span>
                    </FileItem>
                  ))}
                </AddressContainer>
              </AddressList>
            )}
          </div>

          {/* Other Churches input */}
          <div style={{ marginBottom: '2rem' }}>
            <h3>Other Churches</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <input
                type="color"
                value={competitiveChurchColor}
                onChange={e => setCompetitiveChurchColor(e.target.value)}
                style={{ width: 32, height: 32, border: 'none', borderRadius: '50%', cursor: 'pointer' }}
              />
              <span style={{ color: '#666', fontSize: '0.9rem' }}>Pin color</span>
            </div>
            <div style={{ marginBottom: '0.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.25rem', color: '#333', fontSize: '0.9rem' }}>
                Travel Time Radius:
              </label>
              <select
                value={competitiveTravelTimeRadius}
                onChange={e => setCompetitiveTravelTimeRadius(Number(e.target.value))}
                style={{ width: '100%', padding: '0.5rem', borderRadius: 4, border: '1px solid #ccc' }}
              >
                <option value={0}>None</option>
                <option value={5}>5 minutes</option>
                <option value={10}>10 minutes</option>
                <option value={15}>15 minutes</option>
                <option value={20}>20 minutes</option>
              </select>
            </div>
            

            
            {/* Manual file input */}
            <div style={{marginBottom: '1rem'}}>
              <p style={{fontSize: '0.9rem', color: '#666', marginBottom: '0.5rem'}}>
                Select Excel file with other church addresses:
              </p>
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={(e) => {
                  const files = e.target.files;
                  if (files && files.length > 0) {
                    console.log('Manual competitive church file selection:', files[0]);
                    onCompetitiveDrop([files[0]]);
                  }
                }}
                style={{
                  fontSize: '0.8rem',
                  padding: '0.5rem',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  backgroundColor: '#f9f9f9',
                  width: '100%'
                }}
              />
              {isLoadingCompetitive && (
                <div style={{ 
                  padding: '0.5rem', 
                  backgroundColor: '#fff3cd', 
                  borderRadius: 4,
                  fontSize: '0.9rem',
                  color: '#856404',
                  marginTop: '0.5rem'
                }}>
                  Processing file...
                </div>
              )}
            </div>

            {competitiveChurchData.length > 0 && (
              <div style={{ 
                marginTop: '1rem', 
                padding: '0.5rem', 
                backgroundColor: '#f8f9fa', 
                borderRadius: 4,
                fontSize: '0.9rem'
              }}>
                <div style={{ color: '#28a745', marginBottom: '0.25rem' }}>
                  Within radius: {competitiveAddressesInRadius} addresses
                </div>
                <div style={{ color: '#dc3545' }}>
                  Outside radius: {competitiveAddressesOutsideRadius} addresses
                </div>
              </div>
            )}

            {competitiveChurchData.length > 0 && (
              <div style={{ marginTop: '1rem' }}>
                <h4 style={{ margin: '0 0 0.5rem 0', color: '#333', fontSize: '0.9rem' }}>Uploaded Files:</h4>
                {competitiveChurchData.map((file) => (
                  <div key={file.id} style={{ 
                    padding: '0.5rem', 
                    backgroundColor: '#f0f0f0', 
                    borderRadius: 4, 
                    marginBottom: '0.5rem',
                    fontSize: '0.8rem'
                  }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>{file.name}</div>
                    <div>{file.addresses.length} addresses • {file.markers.length} geocoded</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Population Density Controls */}
          <div style={{ marginBottom: '2rem' }}>
            <h3>Population Density</h3>
            <div style={{ marginBottom: '0.5rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={isPopulationHeatMapVisible}
                  onChange={(e) => {
                    setIsPopulationHeatMapVisible(e.target.checked);
                    if (e.target.checked && !populationData && !isLoadingPopulationData) {
                      loadPopulationData();
                    }
                  }}
                  style={{ width: '16px', height: '16px' }}
                />
                <span style={{ color: '#333', fontSize: '0.9rem' }}>
                  Show population heat map {populationData && '(Data loaded)'}
                </span>
              </label>
            </div>
            
            {isPopulationHeatMapVisible && (
              <>
                <div style={{ marginBottom: '0.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.25rem', color: '#333', fontSize: '0.9rem' }}>
                    Heat Map Opacity:
                  </label>
                  <input
                    type="range"
                    min="0.1"
                    max="1"
                    step="0.1"
                    value={populationHeatMapOpacity}
                    onChange={(e) => setPopulationHeatMapOpacity(parseFloat(e.target.value))}
                    style={{ width: '100%' }}
                  />
                  <span style={{ fontSize: '0.8rem', color: '#666' }}>
                    {Math.round(populationHeatMapOpacity * 100)}%
                  </span>
                </div>
                
                {isLoadingPopulationData && (
                  <div style={{ 
                    padding: '0.5rem', 
                    backgroundColor: '#fff3cd', 
                    borderRadius: 4,
                    fontSize: '0.9rem',
                    color: '#856404'
                  }}>
                    Loading 2020 Census population data...
                  </div>
                )}
                
                {populationData && (
                  <div style={{ 
                    marginTop: '0.5rem', 
                    padding: '0.5rem', 
                    backgroundColor: '#f8f9fa', 
                    borderRadius: 4,
                    fontSize: '0.9rem'
                  }}>
                    <div style={{ color: '#28a745', marginBottom: '0.25rem' }}>
                      Total Population: {populationData.totalPopulation.toLocaleString()}
                    </div>
                    <div style={{ color: '#666' }}>
                      Census Tracts: {populationData.tracts.length}
                    </div>
                    <div style={{ color: '#666' }}>
                      Avg Density: {Math.round(populationData.averageDensity).toLocaleString()}/sq mi
                    </div>
                    <div style={{ color: '#666', marginTop: '0.25rem' }}>
                      <strong>Density Levels:</strong>
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#666' }}>
                      <div style={{ color: '#008000' }}>● Very Low (Bottom 20%) - Green</div>
                      <div style={{ color: '#ffff00' }}>● Low (20-40%) - Yellow</div>
                      <div style={{ color: '#ffc0cb' }}>● Medium (40-60%) - Pink</div>
                      <div style={{ color: '#ff0000' }}>● High (60-80%) - Red</div>
                      <div style={{ color: '#800080' }}>● Very High (Top 20%) - Purple</div>
                    </div>
                    <div style={{ fontSize: '0.7rem', color: '#999', marginTop: '0.25rem' }}>
                      <div>Density measured in people per square mile</div>
                      <div>Based on 2020 Census tract data</div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>


        </Sidebar>

        <MapContainer>
          <Map ref={mapRef} />
          <PopulationHeatMap
            map={map}
            populationData={populationData}
            isVisible={isPopulationHeatMapVisible}
            opacity={populationHeatMapOpacity}
          />
        </MapContainer>
      </MainContent>
    </AppContainer>
  );
};

// Styled Components
const AppContainer = styled.div`
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f5f5f5;
`;

const Header = styled.header`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 1rem 2rem;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
`;

const Title = styled.h1`
  margin: 0;
  font-size: 2rem;
  font-weight: 700;
`;

const Subtitle = styled.p`
  margin: 0.5rem 0 0 0;
  opacity: 0.9;
  font-size: 1rem;
`;

const MainContent = styled.main`
  flex: 1;
  display: flex;
  overflow: hidden;
`;

const Sidebar = styled.aside`
  width: 350px;
  background: white;
  border-right: 1px solid #e0e0e0;
  overflow-y: auto;
  padding: 1.5rem;
`;





const StatsContainer = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 6px;
  
  h4 {
    margin: 0 0 0.5rem 0;
    color: #333;
  }
  
  p {
    margin: 0.25rem 0;
    color: #666;
  }
`;

const AddressList = styled.div`
  h4 {
    margin: 0 0 1rem 0;
    color: #333;
  }
`;

const AddressContainer = styled.div`
  max-height: 300px;
  overflow-y: auto;
`;



const FileItem = styled.div`
  padding: 0.75rem;
  border-bottom: 1px solid #f0f0f0;
  
  &:last-child {
    border-bottom: none;
  }
  
  span {
    color: #666;
    font-size: 0.9rem;
  }
`;

const FileHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.25rem;
  
  strong {
    color: #333;
    flex: 1;
  }
`;

const ColorIndicator = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid #fff;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
  margin-left: 0.5rem;
`;



const MapContainer = styled.div`
  flex: 1;
  position: relative;
`;

const Map = styled.div`
  width: 100%;
  height: 100%;
`;

export default App; 